import type { HTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

export interface Crumb {
  label: string;
  to?: string;
}

/** The page header. Title in Schibsted Grotesk, identity in mono directly beneath it:
    the slug, the chat id, the SHA — whatever a reviewer would paste into a
    terminal is always the mono line. */
export function PageHeader({
  crumbs,
  title,
  identity,
  description,
  actions,
  state,
  children,
}: {
  crumbs?: Crumb[];
  title: string;
  identity?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  /** State chips shown inline with the title. */
  state?: ReactNode;
  /** A full-width row under the header proper — the visibility readout. */
  children?: ReactNode;
}) {
  return (
    <header className="border-b border-rule bg-surface px-4 pb-3 pt-3.5 sm:px-6">
      {crumbs && crumbs.length > 0 ? (
        <nav aria-label="Breadcrumb" className="mb-1.5">
          <ol className="m-0 flex list-none flex-wrap items-center gap-1.5 p-0">
            {crumbs.map((crumb, index) => (
              <li key={`${crumb.label}-${index}`} className="flex items-center gap-1.5">
                {index > 0 ? (
                  <span aria-hidden className="text-ink-3">
                    /
                  </span>
                ) : null}
                {crumb.to ? (
                  <Link to={crumb.to} className="eyebrow hover:text-ink">
                    {crumb.label}
                  </Link>
                ) : (
                  <span className="eyebrow">{crumb.label}</span>
                )}
              </li>
            ))}
          </ol>
        </nav>
      ) : null}

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <h1 className="text-xl font-semibold tracking-[-0.015em] text-ink">{title}</h1>
            {state}
          </div>
          {identity ? <div className="mt-1 font-mono text-xs text-ink-2">{identity}</div> : null}
          {description ? <p className="mt-1.5 max-w-prose text-sm leading-[19px] text-ink-2">{description}</p> : null}
        </div>
        {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
      </div>

      {children ? <div className="mt-2.5 border-t border-rule pt-2.5">{children}</div> : null}
    </header>
  );
}

export function PageBody({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('px-4 py-4 sm:px-6', className)} {...props} />;
}

/** The console's detail layout: the working column, and a fixed-width column of
    facts beside it. The facts column is the gauge cluster — the same fields in
    the same order on every review, so a reviewer's eye learns where to look for
    the claim, the gate and the deploys once. */
export function DetailLayout({ main, aside }: { main: ReactNode; aside: ReactNode }) {
  return (
    <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-[minmax(0,1fr)_310px]">
      <div className="min-w-0 space-y-4">{main}</div>
      <div className="space-y-4 xl:sticky xl:top-4">{aside}</div>
    </div>
  );
}
